/**
 * Sign-in domain selection logic.
 *
 * When a deployment provides VITE_ENTRA_DOMAIN_HINT (?domain_hint=), Entra restricts
 * sign-in to that domain, which locks out EAIGLE staff. Internal users therefore need the
 * domain_hint parameter omitted entirely - INTERNAL_DOMAIN_LABEL is only shown
 * in the picker, it is never sent to Entra.
 */
import { safeGetItem, safeSetItem } from "@eaigle/utils/storage-helpers";
import { oauthEnv } from "./oauthEnv";

/** Display-only label for internal sign-in. Internal users send no domain_hint. */
export const INTERNAL_DOMAIN_LABEL = "eaigle.ai";

/** localStorage key: which sign-in domain the user last picked. */
const SIGN_IN_DOMAIN_KEY = "signInDomain";

export type SignInDomainMode = "tenant" | "internal";

export type SignInDomainOption = {
  mode: SignInDomainMode;
  label: string;
};

/** True when the deployment provides a domain hint (=> customer vs internal domain choice). */
export function isSignInDomainSelectable(): boolean {
  return oauthEnv.domainHint !== "";
}

/**
 * Domain picker items.
 * Returns an empty list when the deployment doesn't have a domain hint.
 */
export function signInDomainOptions(): SignInDomainOption[] {
  if (!isSignInDomainSelectable()) {
    return [];
  }
  return [
    { mode: "tenant", label: oauthEnv.domainHint },
    { mode: "internal", label: INTERNAL_DOMAIN_LABEL },
  ];
}

/** The persisted choice, defaulting to the deployment's own domain. */
export function getSignInDomain(): SignInDomainMode {
  return safeGetItem(SIGN_IN_DOMAIN_KEY) === "internal" ? "internal" : "tenant";
}

export function setSignInDomain(mode: SignInDomainMode): void {
  safeSetItem(SIGN_IN_DOMAIN_KEY, mode);
}

/**
 * MSAL extraQueryParameters for the current selection, or undefined when no
 * domain_hint should be sent. Resolved per request so a switch takes effect
 * without reconfiguring MSAL.
 */
export function signInExtraQueryParameters(): Record<string, string> | undefined {
  if (!oauthEnv.domainHint || getSignInDomain() === "internal") return undefined;
  return { domain_hint: oauthEnv.domainHint };
}
